import type { AppointmentViewMode } from '../components/AppointmentViewToggle';

export const APPOINTMENT_ID_PARAM = 'appointmentId';

/**
 * Legacy query flag. New opens use history state (`appointmentCreate`), then this
 * key is stripped so the create dialog does not stay in the URL.
 */
const APPOINTMENT_CREATE_PARAM = 'create';

/** Opens the edit dialog for the current `appointmentId` (`?edit=1`). */
export const APPOINTMENT_EDIT_PARAM = 'edit';

const CREATE_PATIENT_PARAM = 'createPatientId';
const CREATE_LEAD_PARAM = 'createLeadId';
const CREATE_CLINIC_PARAM = 'createClinicId';
const CREATE_DOCTOR_PARAM = 'createDoctorId';
const CREATE_DATE_PARAM = 'createDate';
const CREATE_START_PARAM = 'createStartTime';
const CREATE_WARN_PARAM = 'createWarn';

/** Default hub view when `?view=` is missing or invalid. */
export const DEFAULT_APPOINTMENT_VIEW: AppointmentViewMode = 'calendar';

export type AppointmentCreatePrefill = {
  patientId?: string | null;
  leadId?: string | null;
  clinicId?: string | null;
  doctorId?: string | null;
  date?: string | null;
  startTime?: string | null;
  warn?: boolean;
};

/**
 * Cross-page open of the create dialog. Lives on history state, not the query
 * string, so refresh and the address bar stay on the appointments hub.
 */
export type AppointmentHubLocationState = {
  appointmentCreate?: AppointmentCreatePrefill;
};

export const appointmentCreateLocationState = (
  prefill: AppointmentCreatePrefill = {}
): AppointmentHubLocationState => ({
  appointmentCreate: prefill,
});

export const isAppointmentViewMode = (value: string | null): value is AppointmentViewMode =>
  value === 'list' || value === 'calendar' || value === 'kanban';

const isTruthyFlag = (value: string | null): boolean =>
  value === '1' || value === 'true';

export const isAppointmentCreateOpen = (searchParams: URLSearchParams): boolean =>
  isTruthyFlag(searchParams.get(APPOINTMENT_CREATE_PARAM));

export const isAppointmentEditOpen = (searchParams: URLSearchParams): boolean =>
  isTruthyFlag(searchParams.get(APPOINTMENT_EDIT_PARAM));

const readTrimmed = (searchParams: URLSearchParams, key: string): string | null => {
  const value = searchParams.get(key)?.trim() || '';
  return value || null;
};

export const parseAppointmentCreatePrefill = (
  searchParams: URLSearchParams
): AppointmentCreatePrefill => ({
  patientId: readTrimmed(searchParams, CREATE_PATIENT_PARAM),
  leadId: readTrimmed(searchParams, CREATE_LEAD_PARAM),
  clinicId: readTrimmed(searchParams, CREATE_CLINIC_PARAM),
  doctorId: readTrimmed(searchParams, CREATE_DOCTOR_PARAM),
  date: readTrimmed(searchParams, CREATE_DATE_PARAM),
  startTime: readTrimmed(searchParams, CREATE_START_PARAM),
  warn: isTruthyFlag(searchParams.get(CREATE_WARN_PARAM)),
});

/** Drop legacy create-dialog query keys. Opening the dialog no longer writes them. */
export const stripAppointmentCreateParams = (params: URLSearchParams): void => {
  params.delete(APPOINTMENT_CREATE_PARAM);
  params.delete(CREATE_PATIENT_PARAM);
  params.delete(CREATE_LEAD_PARAM);
  params.delete(CREATE_CLINIC_PARAM);
  params.delete(CREATE_DOCTOR_PARAM);
  params.delete(CREATE_DATE_PARAM);
  params.delete(CREATE_START_PARAM);
  params.delete(CREATE_WARN_PARAM);
};

/** Build /appointments URL. View, detail, and edit stay in the query string. */
export const buildAppointmentsUrl = (options: {
  view?: AppointmentViewMode | string | null;
  appointmentId?: string | null;
  /** With `appointmentId`, open the edit dialog instead of only the detail drawer. */
  edit?: boolean;
} = {}): string => {
  const params = new URLSearchParams();
  const view = isAppointmentViewMode(options.view ?? null)
    ? options.view!
    : DEFAULT_APPOINTMENT_VIEW;
  params.set('view', view);
  if (options.appointmentId) {
    params.set(APPOINTMENT_ID_PARAM, options.appointmentId);
    if (options.edit) params.set(APPOINTMENT_EDIT_PARAM, '1');
  }
  return `/appointments?${params.toString()}`;
};

/**
 * Set or clear appointmentId on existing search params (keeps view and other keys).
 * Opening a detail closes the edit dialog and drops any leftover create query keys.
 */
export const withAppointmentIdParam = (
  searchParams: URLSearchParams,
  appointmentId: string | null
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  stripAppointmentCreateParams(next);
  if (appointmentId) {
    next.set(APPOINTMENT_ID_PARAM, appointmentId);
    next.delete(APPOINTMENT_EDIT_PARAM);
  } else {
    next.delete(APPOINTMENT_ID_PARAM);
    next.delete(APPOINTMENT_EDIT_PARAM);
  }
  return next;
};

/**
 * Open or close the edit dialog on existing search params.
 * Opening edit keeps `appointmentId` (drawer returns when the dialog closes).
 * Closing edit clears only `edit`.
 */
export const withAppointmentEditParam = (
  searchParams: URLSearchParams,
  appointmentId: string | null
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  stripAppointmentCreateParams(next);
  if (appointmentId) {
    next.set(APPOINTMENT_ID_PARAM, appointmentId);
    next.set(APPOINTMENT_EDIT_PARAM, '1');
  } else {
    next.delete(APPOINTMENT_EDIT_PARAM);
  }
  return next;
};
